/**
 * PublicSharePage — Premium redesign (Feb 2026)
 * -------------------------------------------------------------------
 * Route: /share/:token
 * Backend: GET /api/public/document-share/:token
 *
 * This is the actual "patient portal" URL clinics send via WhatsApp/SMS
 * (viyora.com/share/<token>). It shows a *curated* set of documents the
 * clinic explicitly shared — not the full patient record — but the visual
 * experience must feel like a premium secure health vault (Apple Health /
 * Notion / Stripe aesthetic) — matching /patient-record/:token.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "isomorphic-dompurify";
import {
  Loader2, FileText, Receipt, Award, ShieldCheck, Camera, Image as ImageIcon,
  FileDown, Calendar, Phone, AlertTriangle, Stethoscope, Pill, HeartPulse,
  MessageCircle, MapPin, Sparkles, Fingerprint, Lock, Shield, IndianRupee,
  Download, Eye, PenSquare, X, Printer,
} from "lucide-react";
import SEO from "@/components/SEO";
import { apiError } from "@/lib/apiError";

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BASE}/api` });

const KIND_META = {
  prescription:    { icon: Pill,        tone: "clay", label: "Prescription" },
  invoice:         { icon: Receipt,     tone: "pine", label: "Invoice" },
  payment_receipt: { icon: Receipt,     tone: "pine", label: "Receipt" },
  treatment_plan:  { icon: Stethoscope, tone: "clay", label: "Treatment plan" },
  treatment_summary:{icon: Stethoscope, tone: "pine", label: "Treatment summary" },
  consent:         { icon: PenSquare,   tone: "pine", label: "Consent" },
  warranty:        { icon: Award,       tone: "clay", label: "Warranty" },
  post_op:         { icon: FileText,    tone: "pine", label: "Post-op instructions" },
  medical_history: { icon: HeartPulse,  tone: "clay", label: "Medical history" },
  xray:            { icon: Camera,      tone: "pine", label: "X-ray" },
  clinical_photo:  { icon: ImageIcon,   tone: "pine", label: "Clinical photo" },
  lab_report:      { icon: FileText,    tone: "pine", label: "Lab report" },
  complete_record: { icon: FileText,    tone: "pine", label: "Complete record" },
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 5)  return "Good evening";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

/* ============================ page ============================ */
export default function PublicSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [pdf, setPdf] = useState(null);

  useEffect(() => {
    api.get(`/public/document-share/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e, "This link may have expired or been revoked.")));
  }, [token]);

  if (err) return <ErrorState message={err} />;
  if (!data) return <LoadingState />;

  const { share, clinic, patient, documents } = data;

  const attachmentUrl = (idx) => `${BASE}/api/public/document-share/${token}/attachment/${idx}`;
  const fileUrl       = (idx) => `${BASE}/api/public/document-share/${token}/file/${idx}`;
  const openPdf = (url, title) => setPdf({ url, title });

  return (
    <div className="min-h-screen bg-[#F6F5F1] text-ink pb-24" data-testid="public-share-page">
      <SEO title={`Documents · ${clinic.name || "Viyora"}`} noindex path={`/share/${token}`} />
      <BackgroundOrnaments />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 space-y-6">
        <HeroCard share={share} clinic={clinic} patient={patient} documents={documents} />
        {share.message && <MessageCard message={share.message} />}
        <DocumentsList
          documents={documents}
          attachmentUrl={attachmentUrl}
          fileUrl={fileUrl}
          openPdf={openPdf}
        />
        <SecurityFooter clinic={clinic} patient={patient} />
      </div>

      {pdf && <PdfViewerModal {...pdf} onClose={() => setPdf(null)} />}
    </div>
  );
}

function BackgroundOrnaments() {
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-gradient-to-br from-[#1A362B]/8 to-transparent blur-3xl" />
      <div className="absolute -top-16 right-0 w-[440px] h-[440px] rounded-full bg-gradient-to-br from-[#D06A42]/10 to-transparent blur-3xl" />
      <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-[#B89C81]/8 to-transparent blur-3xl" />
    </div>
  );
}

/* ============================ HERO ============================ */
function HeroCard({ share, clinic, patient, documents }) {
  const name = patient?.name || "there";
  const firstName = (name.split(" ")[0]) || name;

  // Group document counts by kind for the stat pills.
  const counts = useMemo(() => {
    const acc = { treatments: 0, invoices: 0, prescriptions: 0,
                  consents: 0, warranties: 0, xrays: 0, other: 0 };
    for (const d of documents || []) {
      switch (d.kind) {
        case "treatment_plan":
        case "treatment_summary": acc.treatments++;    break;
        case "invoice":
        case "payment_receipt":   acc.invoices++;      break;
        case "prescription":      acc.prescriptions++; break;
        case "consent":           acc.consents++;      break;
        case "warranty":          acc.warranties++;    break;
        case "xray":
        case "clinical_photo":    acc.xrays++;         break;
        default:                  acc.other++;
      }
    }
    return acc;
  }, [documents]);

  const stats = [
    { label: "Treatments",    value: counts.treatments,    icon: Stethoscope },
    { label: "Invoices",      value: counts.invoices,      icon: Receipt },
    { label: "Prescriptions", value: counts.prescriptions, icon: Pill },
    { label: "Consents",      value: counts.consents,      icon: PenSquare },
    { label: "Warranties",    value: counts.warranties,    icon: Award },
    { label: "X-rays",        value: counts.xrays,         icon: Camera },
  ];

  const phone = clinic?.phone || "";
  const digits = phone.replace(/[^\d+]/g, "");
  const waNumber = digits.replace(/^\+/, "");
  const waMessage = encodeURIComponent(`Hello ${clinic?.name || "Doctor"}, this is ${name}.`);
  const mapUrl = clinic?.google_maps_url
    || (clinic?.address ? `https://maps.google.com/?q=${encodeURIComponent(clinic.address)}` : null);
  const expiresLabel = share.expires_at ? fmtDate(share.expires_at) : null;

  return (
    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1A362B] via-[#264A3B] to-[#12291F] text-linen shadow-[0_30px_80px_-30px_rgba(26,54,43,0.55)]"
             data-testid="share-hero">
      <div className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
           style={{ backgroundImage: "radial-gradient(rgba(255,255,255,.35) 1px, transparent 1px)", backgroundSize: "3px 3px" }} />
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/5" />
      <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-white/5" />

      <div className="relative p-6 sm:p-10">
        {/* Top row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <ClinicLogo clinic={clinic} />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] text-linen/60">Secure health vault</div>
              <div className="font-display text-xl sm:text-2xl truncate">{clinic?.name || "Your clinic"}</div>
            </div>
          </div>
          <div className="hidden sm:flex flex-col items-end text-xs text-linen/60 shrink-0">
            <span className="inline-flex items-center gap-1 bg-white/10 px-3 py-1 rounded-full whitespace-nowrap">
              <ShieldCheck className="w-3.5 h-3.5" /> Encrypted portal
            </span>
            {expiresLabel && (
              <span className="mt-2">Link valid until <span className="text-linen">{expiresLabel}</span></span>
            )}
          </div>
        </div>

        {/* Greeting */}
        <div className="mt-8 sm:mt-10">
          <div className="text-linen/60 text-sm">{greeting()},</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
              data-testid="share-greeting">
            {firstName} <span aria-hidden="true">👋</span>
          </h1>
          <p className="mt-3 max-w-lg text-linen/75 text-sm sm:text-base">
            Welcome to your secure dental health vault. Everything about your care lives here,
            organised and always available.
          </p>
        </div>

        {/* Stat pills */}
        <div className="mt-8 grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3" data-testid="share-stats">
          {stats.map((s) => (
            <div key={s.label}
                 className="rounded-2xl bg-white/8 backdrop-blur-sm border border-white/10 px-3 py-3 sm:px-4 sm:py-4 flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-linen/60 text-[10px] uppercase tracking-widest">
                <s.icon className="w-3 h-3" />
                <span className="truncate">{s.label}</span>
              </div>
              <div className="font-display text-2xl sm:text-3xl leading-none tabular-nums">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap gap-2 sm:gap-3" data-testid="share-actions">
          {digits && (
            <a href={`tel:${digits}`} data-testid="btn-call-clinic"
               className="btn-hero btn-hero-primary"><Phone className="w-4 h-4" /> Call clinic</a>
          )}
          {waNumber && (
            <a href={`https://wa.me/${waNumber}?text=${waMessage}`} target="_blank" rel="noopener noreferrer"
               data-testid="btn-whatsapp-clinic" className="btn-hero btn-hero-ghost">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          )}
          {mapUrl && (
            <a href={mapUrl} target="_blank" rel="noopener noreferrer" data-testid="btn-directions"
               className="btn-hero btn-hero-ghost"><MapPin className="w-4 h-4" /> Directions</a>
          )}
          {clinic?.slug && (
            <a href={`/book/${clinic.slug}`} target="_blank" rel="noopener noreferrer" data-testid="btn-book-appointment"
               className="btn-hero btn-hero-ghost"><Calendar className="w-4 h-4" /> Book appointment</a>
          )}
        </div>

        {patient.phone_last4 && (
          <div className="mt-6 text-[11px] text-linen/50 flex items-center gap-3">
            <span>Phone ••••{patient.phone_last4}</span>
            {expiresLabel && <span className="sm:hidden">· Link valid until {expiresLabel}</span>}
          </div>
        )}
      </div>

      <style>{`
        .btn-hero {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 0.65rem 1.1rem; border-radius: 999px; font-size: 0.875rem;
          font-weight: 500; transition: transform .15s ease, background .2s ease;
          white-space: nowrap;
        }
        .btn-hero:active { transform: scale(.97); }
        .btn-hero-primary { background: #F5F3ED; color: #1A362B; }
        .btn-hero-primary:hover { background: #ffffff; }
        .btn-hero-ghost { background: rgba(255,255,255,.08); color: #F5F3ED; border: 1px solid rgba(255,255,255,.14); }
        .btn-hero-ghost:hover { background: rgba(255,255,255,.14); }
        .btn-hero-accent { background: #D06A42; color: #fff; }
        .btn-hero-accent:hover { background: #E17550; }
      `}</style>
    </section>
  );
}

function ClinicLogo({ clinic }) {
  const size = "w-16 h-16";
  if (clinic?.logo_url) {
    return <img src={clinic.logo_url} alt={clinic.name}
                className={`${size} rounded-2xl object-cover bg-white/90 shadow-lg shadow-black/20`} />;
  }
  const letter = (clinic?.name || "V").trim()[0]?.toUpperCase() || "V";
  return (
    <div className={`${size} rounded-2xl grid place-items-center bg-white text-[#1A362B] font-display text-lg font-semibold shadow-lg shadow-black/20`}>
      {letter}
    </div>
  );
}

/* ============================ MESSAGE CARD ============================ */
function MessageCard({ message }) {
  return (
    <section className="rounded-3xl bg-white/70 backdrop-blur-md border border-[#E7E1D3] p-5 sm:p-6 shadow-sm"
             data-testid="share-message">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#D06A42]" />
        <div className="text-[11px] uppercase tracking-[0.22em] text-ink-mid">A note from your clinic</div>
      </div>
      <p className="text-sm text-ink/80 whitespace-pre-wrap leading-relaxed">{message}</p>
    </section>
  );
}

/* ============================ DOCUMENTS LIST ============================ */
function DocumentsList({ documents, attachmentUrl, fileUrl, openPdf }) {
  if (!documents?.length) {
    return (
      <EmptyState icon={FileText} title="No documents in this share"
                  hint="If you were expecting something here, please contact your clinic." />
    );
  }
  return (
    <section className="space-y-4" data-testid="share-documents">
      <div className="flex items-center gap-2 px-1">
        <FileText className="w-4 h-4 text-ink" />
        <div className="text-[11px] uppercase tracking-[0.22em] text-ink-mid">Shared documents</div>
        <div className="ml-auto text-xs text-ink-soft">{documents.length} item{documents.length === 1 ? "" : "s"}</div>
      </div>
      {documents.map((doc) => (
        <DocumentCard key={doc.index}
                      doc={doc}
                      attachmentUrl={attachmentUrl}
                      fileUrl={fileUrl}
                      openPdf={openPdf} />
      ))}
    </section>
  );
}

function DocumentCard({ doc, attachmentUrl, fileUrl, openPdf }) {
  const meta = KIND_META[doc.kind] || { icon: FileText, tone: "pine", label: doc.kind };
  const Icon = meta.icon;
  const pdfUrl  = doc.pdf_attachable ? attachmentUrl(doc.index) : null;
  const imgUrl  = ["xray", "clinical_photo", "lab_report"].includes(doc.kind) ? fileUrl(doc.index) : null;

  return (
    <article className="rounded-2xl bg-white/85 backdrop-blur border border-[#E7E1D3] p-5 shadow-sm"
             data-testid={`share-doc-card-${doc.kind}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${
            meta.tone === "clay" ? "bg-[#FDF3ED] text-[#D06A42]" : "bg-[#F0EBDD] text-ink"}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft">{meta.label}</div>
            <h3 className="font-display text-lg text-ink leading-tight truncate">{doc.title}</h3>
            {doc.date && (
              <div className="text-xs text-ink-mid mt-0.5 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {fmtDate(doc.date)}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 shrink-0">
          {pdfUrl && (
            <>
              <ActionBtn icon={Eye} onClick={() => openPdf(pdfUrl, doc.title)}>View</ActionBtn>
              <ActionBtn icon={Download} as="a" href={pdfUrl} download>Download</ActionBtn>
            </>
          )}
          {imgUrl && !pdfUrl && (
            <>
              <ActionBtn icon={Eye} as="a" href={imgUrl} target="_blank" rel="noopener noreferrer">Open</ActionBtn>
              <ActionBtn icon={Download} as="a" href={imgUrl} download>Save</ActionBtn>
            </>
          )}
        </div>
      </div>

      <div className="mt-4">
        <DocumentBody doc={doc} />
      </div>
    </article>
  );
}

function DocumentBody({ doc }) {
  const d = doc.data || {};
  switch (doc.kind) {
    case "prescription":
      return (
        <div className="rounded-xl bg-[#FBFAF6] border border-[#EFEADC] p-3">
          {d.diagnosis && (
            <div className="text-sm mb-2"><span className="text-ink-soft">Diagnosis:</span> <span className="text-ink">{d.diagnosis}</span></div>
          )}
          {d.items?.length ? (
            <div className="divide-y divide-[#EFEADC]">
              {d.items.slice(0, 6).map((m, i) => (
                <div key={m.id || `${doc.index}-${m.drug_name || "med"}-${i}`}
                     className="py-2 grid grid-cols-[auto_1fr_auto] items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#FDF3ED] text-[#D06A42] grid place-items-center">
                    <Pill className="w-3.5 h-3.5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink truncate">
                      {m.drug_name}{m.strength ? ` · ${m.strength}` : ""}
                    </div>
                    <div className="text-xs text-ink-mid truncate">{m.regimen}</div>
                  </div>
                  <div className="text-xs text-ink-soft whitespace-nowrap">{m.quantity || ""}</div>
                </div>
              ))}
              {d.items.length > 6 && (
                <div className="py-2 text-xs text-ink-soft">+{d.items.length - 6} more medicines — see PDF</div>
              )}
            </div>
          ) : null}
        </div>
      );

    case "invoice":
    case "payment_receipt":
      return (
        <div className="rounded-xl bg-gradient-to-br from-[#FBFAF6] to-[#F5F0E3] border border-[#EFEADC] p-4">
          <div className="text-[10px] uppercase tracking-widest text-ink-soft">Total amount</div>
          <div className="mt-1 font-display text-3xl text-ink flex items-center">
            <IndianRupee className="w-6 h-6" />{Number(d.total || 0).toLocaleString("en-IN")}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-mid">
            <span>{(d.items || []).length} line item{(d.items || []).length === 1 ? "" : "s"}</span>
            {d.status && <span className="capitalize">Status · {d.status.replace(/_/g, " ")}</span>}
            {d.payment_method && <span>via {d.payment_method}</span>}
          </div>
        </div>
      );

    case "warranty":
      return (
        <div className="rounded-xl bg-gradient-to-br from-[#FFFDF7] to-[#F5F0E3] border border-[#EFEADC] p-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <FieldTile label="Procedure" value={`${d.procedure || "—"}${d.variant ? " · " + d.variant : ""}`} />
            <FieldTile label="Duration"  value={d.years_valid ? `${d.years_valid} years` : "—"} />
            <FieldTile label="Issued"    value={fmtDate(d.issue_date) || "—"} />
            <FieldTile label="Valid till" value={fmtDate(d.valid_till) || "—"} />
          </div>
        </div>
      );

    case "post_op":
      return d.content_html ? (
        <div
          className="text-sm text-ink/80 rounded-xl bg-[#FBFAF6] border border-[#EFEADC] p-4 prose prose-sm max-w-none"
          // Backend markup is sanitised — never trust remote HTML.
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(d.content_html || "", { USE_PROFILES: { html: true } }),
          }}
        />
      ) : <div className="text-sm text-ink-mid">Full post-op instructions attached as PDF.</div>;

    case "consent":
      return (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <FieldTile label="Status"        value={d.status || "—"} />
          <FieldTile label="Signed on"     value={fmtDate(d.patient_signed_at) || "—"} />
          <FieldTile label="Countersigned" value={fmtDate(d.doctor_signed_at) || "—"} />
          <FieldTile label="IP address"    value={d.signed_ip || "—"} mono />
          <div className="col-span-2 rounded-xl bg-[#F0EBDD]/60 border border-[#EFEADC] px-3 py-2 flex items-center gap-2 text-xs text-ink-mid">
            <Fingerprint className="w-3.5 h-3.5" /> Legally binding · audit trail preserved
          </div>
        </div>
      );

    case "treatment_plan":
      return (
        <div className="rounded-xl bg-[#FBFAF6] border border-[#EFEADC] p-4 text-sm text-ink/80">
          {d.procedure && (
            <div className="font-medium text-ink mb-1">
              {d.procedure}{d.variant ? ` · ${d.variant}` : ""}{d.tooth ? ` · Tooth ${d.tooth}` : ""}
            </div>
          )}
          {d.notes && <div className="text-ink-mid mb-2">{d.notes}</div>}
          {d.cost != null && (
            <div className="text-xs text-ink-mid">Estimated: <span className="text-ink font-medium">{INR(d.cost)}</span></div>
          )}
        </div>
      );

    case "treatment_summary":
      return (
        <div className="rounded-xl bg-[#FBFAF6] border border-[#EFEADC] p-3 divide-y divide-[#EFEADC]">
          {(d.treatments || []).slice(0, 8).map((t, i) => (
            <div key={t.id || `${doc.index}-tx-${i}`}
                 className="py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-ink truncate">
                  {t.procedure}{t.variant ? ` · ${t.variant}` : ""}
                </div>
                <div className="text-xs text-ink-soft">{fmtDate(t.date)} · <span className="capitalize">{(t.status || "").replace(/_/g, " ")}</span></div>
              </div>
              <div className="text-sm text-ink tabular-nums whitespace-nowrap">{INR(t.cost)}</div>
            </div>
          ))}
          {(d.treatments || []).length === 0 && (
            <div className="py-3 text-sm text-ink-soft">No treatments recorded yet.</div>
          )}
        </div>
      );

    case "medical_history":
      return (
        <div className="grid grid-cols-2 gap-3">
          <FieldTile label="Allergies"    value={d.allergies || "None reported"} />
          <FieldTile label="Blood group"  value={d.blood_group || "—"} />
          <FieldTile label="Date of birth" value={fmtDate(d.dob) || "—"} />
          <FieldTile label="Gender"       value={d.gender || "—"} />
          {d.medical_notes && (
            <div className="col-span-2 text-sm text-ink-mid bg-[#FBFAF6] border border-[#EFEADC] rounded-xl p-3">
              {d.medical_notes}
            </div>
          )}
        </div>
      );

    case "xray":
    case "clinical_photo":
    case "lab_report":
      return (
        <div className="rounded-xl bg-[#FBFAF6] border border-[#EFEADC] p-3 text-sm text-ink-mid flex items-center gap-2">
          <Camera className="w-4 h-4 text-ink-soft" />
          <span className="truncate">{d.filename || "Image file"}</span>
          <span className="text-xs text-ink-soft ml-auto">{d.content_type}</span>
        </div>
      );

    case "complete_record":
      return (
        <div className="rounded-xl bg-[#FBFAF6] border border-[#EFEADC] p-4 text-sm text-ink-mid">
          Everything in one place — treatments, invoices, warranties, X-rays and consents.
        </div>
      );

    default:
      return null;
  }
}

/* ============================ security footer ============================ */
function SecurityFooter({ clinic, patient }) {
  const badges = [
    { icon: ShieldCheck, label: "Secured by Viyora" },
    { icon: Lock,        label: "End-to-end encrypted" },
    { icon: Shield,      label: "HIPAA-ready" },
    { icon: Fingerprint, label: "Private link · unguessable" },
  ];
  return (
    <footer className="mt-10 rounded-3xl bg-white/70 backdrop-blur border border-[#E7E1D3] p-6"
            data-testid="share-security">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {badges.map((b) => (
          <div key={b.label} className="rounded-2xl bg-[#F0EBDD]/50 border border-[#EFEADC] px-3 py-3 flex items-center gap-2">
            <b.icon className="w-4 h-4 text-ink" />
            <span className="text-xs text-ink/80">{b.label}</span>
          </div>
        ))}
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-6 text-sm text-ink-mid">
        <div>
          {clinic?.phone   && <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> {clinic.phone}</div>}
          {clinic?.address && <div className="flex items-start gap-2 mt-2"><MapPin className="w-4 h-4 mt-0.5" /> {clinic.address}</div>}
          {clinic?.email   && <div className="flex items-start gap-2 mt-2"><MessageCircle className="w-4 h-4 mt-0.5" /> {clinic.email}</div>}
        </div>
        <div className="sm:text-right text-xs text-ink-soft">
          Only you have this private link. Do not share it with anyone.<br />
          Records for {patient?.name || "you"}. Powered by <span className="font-display text-ink">Viyora</span>.
        </div>
      </div>
    </footer>
  );
}

/* ============================ modals + shared ============================ */
function PdfViewerModal({ url, title, onClose }) {
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-3 sm:p-6"
         data-testid="pdf-viewer">
      <div className="w-full max-w-5xl h-[90vh] rounded-2xl bg-white overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#EFEADC]">
          <div className="min-w-0 flex items-center gap-2">
            <FileText className="w-4 h-4 text-ink shrink-0" />
            <div className="font-medium text-ink truncate">{title}</div>
          </div>
          <div className="flex items-center gap-1">
            <ActionBtn icon={Download} as="a" href={url} download tone="ghost">Download</ActionBtn>
            <ActionBtn icon={Printer} as="a" href={url} target="_blank" tone="ghost">Print</ActionBtn>
            <button onClick={onClose} data-testid="pdf-close"
                    className="w-9 h-9 rounded-full hover:bg-[#F0EBDD] grid place-items-center">
              <X className="w-4 h-4 text-ink" />
            </button>
          </div>
        </div>
        <iframe title={title} src={url} className="flex-1 w-full border-0" />
      </div>
    </div>
  );
}

function ActionBtn({ icon: Icon, children, as = "button", tone = "default", ...rest }) {
  const El = as;
  const cls = {
    default: "bg-[#F0EBDD] hover:bg-[#E7DEC5] text-ink",
    clay:    "bg-[#D06A42] hover:bg-[#E17550] text-white",
    ghost:   "bg-transparent hover:bg-[#F0EBDD] text-ink",
  }[tone];
  return (
    <El {...rest}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-colors ${cls}`}>
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
    </El>
  );
}

function FieldTile({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className={`text-sm text-ink mt-0.5 font-medium truncate ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="rounded-3xl bg-white/70 backdrop-blur border border-[#E7E1D3] py-16 text-center" data-testid="empty-state">
      <div className="w-12 h-12 mx-auto rounded-full bg-[#F0EBDD] grid place-items-center mb-3">
        <Icon className="w-5 h-5 text-ink-mid" />
      </div>
      <div className="font-display text-lg text-ink">{title}</div>
      <div className="text-sm text-ink-mid mt-1 max-w-md mx-auto">{hint}</div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-[#F6F5F1] grid place-items-center text-ink-mid">
      <div className="flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin" /> Loading your documents…</div>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="min-h-screen bg-[#F6F5F1] grid place-items-center p-6">
      <div className="max-w-md bg-white rounded-3xl border border-[#E7E1D3] p-8 text-center">
        <AlertTriangle className="w-10 h-10 text-clay mx-auto mb-3" />
        <h1 className="font-display text-2xl text-ink mb-2">Link unavailable</h1>
        <p className="text-sm text-ink-mid">{message}</p>
        <p className="text-xs text-ink-soft mt-4">If you believe this is a mistake, contact your clinic — they can regenerate the link for you.</p>
      </div>
    </div>
  );
}
